import type { Teachable } from "~/types/teachable";

interface TocSection {
   title: string | null;
   id: string | null;
   anchors: unknown[];
}
interface TeachableTocState {
   data: Teachable.Resource | null;
   toc: { prep: TocSection; content: TocSection };
}
interface TeachableReviewState {
   review: string | null;
   rating: number;
}
interface UpdateReviewPayload {
   slug: string;
   data: { rating: number; review?: string | null };
}

export function useTeachable(
   initialState: TeachableTocState = {
      data: null,
      toc: {
         prep: { title: null, id: null, anchors: [] },
         content: { title: null, id: null, anchors: [] },
      },
   },
) {
   const $apiFetch = useNuxtApp().$apiFetch as typeof $fetch;
   const { log, logInfo, logError } = useDebugger();
   const { getProfileContent, setProfileTeachables } = useProfile();
   const { setPageTitle, setResourceType, setCrumbTitle } = useBreadcrumbs();
   const route = useRoute();
   // STATE ========================
   const teachable = useState<TeachableTocState>("teachable", () => initialState);
   const review = useState<TeachableReviewState>(
      "review",
      () => ({ review: null, rating: 0 }),
   );
   const note = useState<string | null>("note", () => null);

   // Setters ========================
   const setRating = (rating: number) => {
      review.value.rating = rating;
   };
   const setReview = (data: TeachableReviewState) => {
      review.value = data;
   };
   // QUERY ========================
   const queryTeachable = async (
      slug: string,
   ): Promise<Teachable.Resource | null> => {
      teachable.value.data = null;
      try {
         const res = await $apiFetch<Teachable.Resource>(`teachables/${slug}`);
         log("Query Teachable: ", res);
         teachable.value.data = res;
         setPageTitle(res.title);
         setResourceType(res?.teachable_type);
         // Real titles for the parent crumbs (avoid startCase(slug)).
         // core-work-teachable: linked core work.
         if (res.core_work) setCrumbTitle("coreWork", res.core_work.title);
         // planning-tool-teachable: a teachable belongs to many units, so pick
         // the one in the current URL. No-op on other teachable routes.
         const unit = res.unit_sequences?.find(
            (u) => u.slug === route.params.planningTool,
         );
         if (unit) setCrumbTitle("unit", unit.title);

         // Best-effort sub-fetch — review may not exist yet, don't fail the whole page
         await getTeachableReview(slug).catch(() => {});
         return teachable.value.data;
      } catch (err) {
         logError("queryTeachable failed", err);
         throw err;
      }
   };

   // Best-effort: review may not exist yet for this teachable.
   // Logs and swallows so the parent page still renders.
   const getTeachableReview = async (slug: string): Promise<void> => {
      try {
         const res = await $apiFetch<Teachable.GetReviewResponse>(
            `teachables/${slug}/review`,
         );
         logInfo("getTeachableReview", res);
         // GetReviewResponse is Review | { message: 'Not Reviewed' }
         if ("rating" in res) {
            setReview({ rating: res.rating, review: res.review });
         }
      } catch (err) {
         logError("getTeachableReview failed", err);
      }
   };

   const updateTeachableReview = async (
      teachableReview: UpdateReviewPayload,
   ): Promise<Teachable.ReviewAckResponse> => {
      try {
         const res = await $apiFetch<Teachable.ReviewAckResponse>(
            `teachables/${teachableReview.slug}/review`,
            {
               method: "POST",
               body: { ...teachableReview.data },
            },
         );
         logInfo("updateTeachableReview", res);
         return res;
      } catch (err) {
         logError("updateTeachableReview failed", err);
         throw err;
      }
   };

   // Best-effort: note may not exist yet for this teachable.
   // Logs and swallows so the parent page still renders.
   const getTeachableNote = async (slug: string): Promise<void> => {
      note.value = null;
      try {
         const data = await $apiFetch<Teachable.GetNoteResponse>(
            `teachables/${slug}/note`,
         );
         note.value =
            data.note && isHtml(data.note) ? htmlToPlainText(data.note) : data.note;
      } catch (err) {
         logError("getTeachableNote failed", err);
      }
   };

   const updateTeachableNote = async (
      slug: string,
   ): Promise<Teachable.NoteAckResponse> => {
      try {
         const data = await $apiFetch<Teachable.NoteAckResponse>(
            `teachables/${slug}/note`,
            {
               method: "POST",
               body: { note: note.value ?? "" },
            },
         );
         note.value = data.note ?? null;
         return data;
      } catch (err) {
         logError("updateTeachableNote failed", err);
         throw err;
      }
   };

   const saveTeachableToProfile = async (
      slug: string,
      method: "POST" | "DELETE" = "POST",
   ): Promise<Teachable.SaveResponse> => {
      try {
         const data = await $apiFetch<Teachable.SaveResponse>(
            `teachables/${slug}/save`,
            { method },
         );
         log(`Profile Teachable ${method}: `, data);
         setProfileTeachables(data);
         return data;
      } catch (err) {
         logError("saveTeachableToProfile failed", err);
         throw err;
      }
   };

   // GETTERS ========================
   const teachableData = computed(() => {
      return teachable.value.data;
   });

   // RETURN ========================
   return {
      queryTeachable,
      teachable: teachableData,
      review,
      setRating,
      setReview,
      getTeachableReview,
      updateTeachableReview,
      note,
      getTeachableNote,
      updateTeachableNote,
      saveTeachableToProfile,
   };
}
